import type { ErJobHealthData, NetworkEobsStatus, StatusBit } from '../types'

import { useQuery } from '@tanstack/react-query'
import { Table, Tag, Card, Typography, Spin, Alert, Empty } from 'antd'

import $api from '../../../API'

const { Title } = Typography

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name'
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: StatusBit) => (
      <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? 'Success' : 'Error'}</Tag>
    )
  },
  {
    title: 'Started At',
    dataIndex: 'started_at',
    key: 'started_at',
    render: (val?: string | null) => (val ? new Date(val).toLocaleString() : '-')
  },
  {
    title: 'Ended At',
    dataIndex: 'ended_at',
    key: 'ended_at',
    render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-')
  },
  {
    title: 'Errors',
    dataIndex: 'errors',
    key: 'errors',
    render: (errors?: string[]) =>
      errors && errors.length > 0 ? (
        errors.map((err, idx) => (
          <Tag color='red' key={idx}>
            {err}
          </Tag>
        ))
      ) : (
        <Tag color='green'>None</Tag>
      )
  }
]

export function ErHealthCheck(): JSX.Element {
  const erQuery = useQuery<ErJobHealthData[]>({
    queryKey: ['healthcheck', 'er'],
    async queryFn({ signal }) {
      const response = await $api.get<ErJobHealthData[]>('/healthcheck/er/status', {
        method: 'GET',
        signal
      })
      return response.data
    }
  })

  const neQuery = useQuery<NetworkEobsStatus>({
    queryKey: ['healthcheck', 'ne'],
    async queryFn({ signal }) {
      const response = await $api.get<NetworkEobsStatus>('/healthcheck/ne/status', {
        method: 'GET',
        signal
      })
      return response.data
    }
  })

  return (
    <div style={{ padding: 20 }}>
      <style>{'.row-error td { background: #fff1f0 !important; }'}</style>

      <Title level={3}>ER Jobs Status</Title>
      {erQuery.isLoading ? (
        <Spin />
      ) : erQuery.isError ? (
        <Alert
          type='error'
          showIcon
          message='Failed to load ER status'
          description={(erQuery.error as Error)?.message ?? 'Unknown error'}
        />
      ) : (erQuery.data?.length ?? 0) === 0 ? (
        <Empty description='No jobs found' />
      ) : (
        <Table
          dataSource={erQuery.data ?? []}
          columns={columns}
          rowKey='name'
          rowClassName={(record: ErJobHealthData) =>
            record.errors && record.errors.length > 0 ? 'row-error' : ''
          }
        />
      )}

      <Title level={3} style={{ marginTop: 40 }}>
        Network EOBs Status
      </Title>
      {neQuery.isLoading ? (
        <Spin />
      ) : neQuery.isError ? (
        <Alert
          type='error'
          showIcon
          message='Failed to load Network EOBs status'
          description={(neQuery.error as Error)?.message ?? 'Unknown error'}
        />
      ) : neQuery.data?.status === 'ok' ? (
        <Card bordered style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
          <Tag color='green'>OK</Tag> Latest records are up to date.
        </Card>
      ) : (
        <Alert
          message='Network EOBs Error'
          description={neQuery.data?.error}
          type='error'
          showIcon
        />
      )}
    </div>
  )
}
